import {computed, Injectable, Signal, signal} from '@angular/core';
import {StorageService} from '@core/services/storage.service';
import {UserProfile} from '@core/services/api/users-api.service';

@Injectable({
    providedIn: 'root'
})
export class PermissionService {
    private storageService: StorageService;
    private profileSignal = signal<string>('');

    constructor(storageService: StorageService) {
        this.storageService = storageService;
        this.loadProfile();
    }

    /**
     * Carrega o perfil do usuário do storage
     */
    private loadProfile(): void {
        const profile = this.storageService.profile;
        this.profileSignal.set(profile);
    }

    /**
     * Atualiza o perfil (útil quando o usuário faz login)
     */
    updateProfile(profile: string): void {
        this.profileSignal.set(profile);
    }

    /**
     * Retorna o perfil atual do usuário
     */
    getCurrentProfile(): string {
        return this.profileSignal();
    }

    /**
     * Verifica se o usuário tem um perfil específico
     */
    hasProfile(profile: string): Signal<boolean> {
        return computed(() => this.profileSignal() === profile);
    }

    /**
     * Verifica se o usuário tem algum dos perfis fornecidos (OR)
     */
    hasAnyProfile(profiles: string[]): Signal<boolean> {
        return computed(() => {
            const currentProfile = this.profileSignal();
            return profiles.includes(currentProfile);
        });
    }

    /**
     * Verifica se o usuário tem todos os perfis fornecidos (AND)
     * Nota: Na prática, um usuário só pode ter um perfil, então isso só retorna true
     * se apenas um perfil for fornecido e ele corresponder ao perfil atual
     */
    hasAllProfiles(profiles: string[]): Signal<boolean> {
        return computed(() => {
            const currentProfile = this.profileSignal();
            if (profiles.length === 1) {
                return profiles[0] === currentProfile;
            }
            // Se múltiplos perfis são requeridos, isso nunca será verdade
            // pois um usuário só pode ter um perfil
            return false;
        });
    }

    /**
     * Verifica se o usuário é ADMIN
     */
    isAdmin(): Signal<boolean> {
        return computed(() => this.profileSignal() === UserProfile.ADMIN);
    }

    /**
     * Verifica se o usuário é PROFESSIONAL
     */
    isProfessional(): Signal<boolean> {
        return computed(() => this.profileSignal() === UserProfile.PROFESSIONAL);
    }

    /**
     * Verifica se o usuário pode acessar um recurso baseado no perfil
     * Alias para hasProfile
     */
    canAccess(profile: string): Signal<boolean> {
        return this.hasProfile(profile);
    }

    /**
     * Verifica se o usuário pode acessar um recurso com qualquer um dos perfis (OR)
     * Alias para hasAnyProfile
     */
    canAccessAny(profiles: string[]): Signal<boolean> {
        return this.hasAnyProfile(profiles);
    }

    /**
     * Verifica se o usuário pode acessar um recurso com todos os perfis (AND)
     * Alias para hasAllProfiles
     */
    canAccessAll(profiles: string[]): Signal<boolean> {
        return this.hasAllProfiles(profiles);
    }
}
